import React, { useState, useRef, useEffect } from 'react';
import { KeyRound } from 'lucide-react';
import useAuthStore from '../../stores/authStore';
import useThemeStore from '../../stores/themeStore';
import useLanguageStore from '../../stores/languageStore';
import { useTranslation } from '../../i18n';
import useChatSessionStore from '../../features/chat/store/chatSessionStore';
import useChatStore from '../../features/chat/store/chatStore';
import useSidebarStore from '../../stores/sidebarStore';
import AboutModal from '../AboutModal/AboutModal';
import MCPTokenPanel from './MCPTokenPanel';
import {
  getCmiInsightsMcpUrl,
  getGoogleMapReviewsMcpUrl,
  getRosettaMcpUrl,
  getYoutubeRetailAnalyzerMcpUrl,
  canAccessRosettaMcp,
  canAccessYoutubeRetailAnalyzer
} from '../../services/mcpTokenService';
// import { useAutoHideScrollbar } from '../../hooks/useAutoHideScrollbar'; // DISABLED FOR TESTING
import './Sidebar.css';

const MCP_TOKEN_PANEL_CONFIGS = {
  google_map_reviews: {
    appId: 'mcp_google_map_reviews',
    serverName: 'mcp_google_map_reviews',
    title: 'Google Map Reviews MCP',
    mcpUrl: getGoogleMapReviewsMcpUrl(),
    defaultRole: 'exporter'
  },
  cmi_insights: {
    appId: 'mcp_cmi_insights',
    serverName: 'mcp_cmi_insights',
    title: 'CMI Insights MCP',
    mcpUrl: getCmiInsightsMcpUrl(),
    defaultRole: 'viewer'
  },
  rosetta: {
    appId: 'mcp_rosetta',
    serverName: 'mcp_rosetta',
    title: 'Rosetta MCP',
    mcpUrl: getRosettaMcpUrl(),
    defaultRole: 'viewer'
  },
  youtube_retail_analyzer: {
    appId: 'mcp_youtube_retail_analyzer',
    serverName: 'mcp_youtube_retail_analyzer',
    title: 'YouTube Retail Analyzer MCP',
    mcpUrl: getYoutubeRetailAnalyzerMcpUrl(),
    defaultRole: 'user'
  }
};

// Typewriter Animation Component for Session Titles
const TypewriterTitle = ({ text, speed = 50 }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, text, speed]);

  return <>{displayText}</>;
};

const Sidebar = ({ onOpenSettings }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showMcpTokenPanel, setShowMcpTokenPanel] = useState(false);
  const [mcpTokenPanelConfig, setMcpTokenPanelConfig] = useState(MCP_TOKEN_PANEL_CONFIGS.google_map_reviews);
  const [hasRosettaAccess, setHasRosettaAccess] = useState(false);
  const [hasYoutubeRetailAnalyzerAccess, setHasYoutubeRetailAnalyzerAccess] = useState(false);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [fadingOutDeleteId, setFadingOutDeleteId] = useState(null);
  const dropdownRef = useRef(null);
  // Auto-hide scrollbar for sessions list
  // const sessionsListRef = useAutoHideScrollbar(3000); // 3 second delay - DISABLED FOR TESTING
  const sessionsListRef = useRef(null);
  const loadingSentinelRef = useRef(null);

  const { user, logout, isAuthenticated, accessToken } = useAuthStore();
  const { theme, setTheme, initTheme } = useThemeStore();
  const { toggleLanguage } = useLanguageStore();
  const t = useTranslation();
  const { isCollapsed, setSidebarCollapsed } = useSidebarStore();
  const {
    sessions,
    isLoading: sessionsLoading,
    isLoadingMore: sessionsLoadingMore,
    hasMore: sessionsHasMore,
    error: sessionsError,
    fetchSessions,
    loadMoreSessions,
    clearError,
    deleteSession,
    deletingSessionId
  } = useChatSessionStore();
  const { loadSessionMessages, currentSessionId } = useChatStore();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Initialize theme on component mount
  useEffect(() => {
    initTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch chat sessions when user becomes authenticated
  // Note: We intentionally only depend on isAuthenticated, not accessToken
  // This prevents unnecessary session refreshes when token is silently refreshed (every 10 min)
  useEffect(() => {
    // Only fetch sessions when user first becomes authenticated
    if (isAuthenticated && accessToken) {
      fetchSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Intentionally omit accessToken and fetchSessions

  useEffect(() => {
    let cancelled = false;

    const loadAnalyzerPermission = async () => {
      if (!isAuthenticated || !accessToken) {
        if (!cancelled) {
          setHasRosettaAccess(false);
          setHasYoutubeRetailAnalyzerAccess(false);
        }
        return;
      }
      if (canAccessRosettaMcp(user) && canAccessYoutubeRetailAnalyzer(user)) {
        if (!cancelled) {
          setHasRosettaAccess(true);
          setHasYoutubeRetailAnalyzerAccess(true);
        }
        return;
      }
      try {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiBase}/api/permissions/me/permissions`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) {
          throw new Error(`Permission request failed: ${response.status}`);
        }
        const data = await response.json();
        const menuCommands = Array.isArray(data.menu_commands) ? data.menu_commands : [];
        if (!cancelled) {
          setHasRosettaAccess(canAccessRosettaMcp(user, menuCommands));
          setHasYoutubeRetailAnalyzerAccess(canAccessYoutubeRetailAnalyzer(user, menuCommands));
        }
      } catch (_error) {
        if (!cancelled) {
          setHasRosettaAccess(false);
          setHasYoutubeRetailAnalyzerAccess(false);
        }
      }
    };

    loadAnalyzerPermission();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, accessToken, user]);

  // Add 'sessions-loaded' class after initial load to prevent re-animation on updates
  useEffect(() => {
    if (!sessionsLoading && sessions.length > 0 && sessionsListRef.current) {
      // Reduced timeout since we're using faster fade-in animation (300ms)
      // Wait 400ms to ensure animation completes smoothly
      const timer = setTimeout(() => {
        if (sessionsListRef.current) {
          sessionsListRef.current.classList.add('sessions-loaded');
        }
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [sessionsLoading, sessions.length]);

  // Intersection Observer for lazy loading more sessions
  useEffect(() => {
    if (!loadingSentinelRef.current || isCollapsed) return;

    const currentSentinel = loadingSentinelRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // When the sentinel is visible and we have more sessions to load
        if (entry.isIntersecting && sessionsHasMore && !sessionsLoadingMore && !sessionsLoading) {
          loadMoreSessions();
        }
      },
      {
        root: sessionsListRef.current,
        rootMargin: '50px', // Start loading 50px before reaching the bottom
        threshold: 0.1
      }
    );

    observer.observe(currentSentinel);

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, [sessionsHasMore, sessionsLoadingMore, sessionsLoading, loadMoreSessions, isCollapsed]);

  // Early return if authentication is invalid to prevent unnecessary renders
  if (!isAuthenticated || !accessToken) {
    return (
      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-user-section">
          <div className="user-avatar">
            <button className="avatar-button" aria-label="User menu">
              <div className="user-avatar-pic">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                  <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="currentColor"/>
                </svg>
              </div>
              {!isCollapsed && <div className="user-name">Please log in</div>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSignOut = () => {
    logout();
    setShowDropdown(false);
  };

  const handleAbout = () => {
    setShowDropdown(false);
    setShowAboutModal(true);
  };

  const handleSettings = () => {
    setShowDropdown(false);
    if (onOpenSettings) {
      onOpenSettings();
    }
  };

  const handleThemeToggle = () => {
    // Toggle between light and dark
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const handleLanguageToggle = () => {
    toggleLanguage();
  };

  // Handle session errors
  const handleRetryFetch = () => {
    const { checkAuth, accessToken } = useAuthStore.getState();
    // Only retry if we have proper authentication
    if (checkAuth() && accessToken && isAuthenticated) {
      clearError();
      fetchSessions();
    }
  };

  // Handle session deletion - first click shows confirm buttons
  const handleDeleteSession = (sessionId, event) => {
    event.stopPropagation(); // Prevent session click
    if (confirmingDeleteId === sessionId) {
      // If already confirming, cancel the confirmation
      setConfirmingDeleteId(null);
    } else {
      // Show confirmation buttons for this session
      setConfirmingDeleteId(sessionId);
    }
  };

  // Handle confirm delete - actually delete the session
  const handleConfirmDelete = async (sessionId, event) => {
    event.stopPropagation(); // Prevent session click

    try {
      const success = await deleteSession(sessionId);

      if (success) {
        // If the deleted session is the currently active session, clear the chat
        if (sessionId === currentSessionId) {
          const { clearChat } = useChatStore.getState();
          clearChat();
        }

        setConfirmingDeleteId(null);
      } else {
        alert('Failed to delete session. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('An error occurred while deleting the session. Please try again.');
    }
  };

  // Handle cancel delete - hide confirmation buttons
  const handleCancelDelete = (event) => {
    event.stopPropagation(); // Prevent session click
    setConfirmingDeleteId(null);
  };

  // Handle mouse leave - reset confirmation state with fade out delay
  const handleSessionMouseLeave = () => {
    if (confirmingDeleteId !== null) {
      // Start fade out animation
      setFadingOutDeleteId(confirmingDeleteId);

      // After fade out completes (300ms), switch back to normal buttons
      setTimeout(() => {
        setConfirmingDeleteId(null);
        setFadingOutDeleteId(null);
      }, 300);
    }
  };

  // Handle session card click to load chat history
  const handleSessionClick = async (sessionId) => {
    if (sessionId === currentSessionId) {
      // Already viewing this session, no need to reload
      return;
    }

    try {
      // IMPORTANT: Before switching sessions, check if there's an ongoing request
      // We need to access the abort controller from ChatInterface
      // Since we can't directly access it, we'll let the ChatInterface handle abortion
      // by checking currentSessionId in the stream handler

      await loadSessionMessages(sessionId);
    } catch (error) {
      console.error('Failed to load session:', error);
      // Could add user notification here
    }
  };

  // Handle new chat (clear current session)
  const handleNewChat = () => {
    window.dispatchEvent(new CustomEvent('datacenterai:new-chat'));
  };

  const handleOpenMcpTokenPanel = (configKey = 'google_map_reviews') => {
    const config = MCP_TOKEN_PANEL_CONFIGS[configKey] || MCP_TOKEN_PANEL_CONFIGS.google_map_reviews;
    setMcpTokenPanelConfig(config);
    if (isCollapsed) {
      setSidebarCollapsed(false);
      setTimeout(() => {
        setShowMcpTokenPanel(true);
      }, 150);
    } else {
      setShowMcpTokenPanel(true);
    }
  };

  // Handle avatar click - expand sidebar if collapsed, then show dropdown
  const handleAvatarClick = () => {
    if (isCollapsed) {
      setSidebarCollapsed(false);
      // Set a small delay to show dropdown after sidebar expands
      setTimeout(() => {
        setShowDropdown(true);
      }, 150);
    } else {
      setShowDropdown(!showDropdown);
    }
  };

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {/* User Avatar Section */}
      <div className="sidebar-user-section">
        <div className="sidebar-header-controls">
          <div className="user-avatar" ref={dropdownRef}>
            <button
              className="avatar-button"
              onClick={handleAvatarClick}
              aria-label="User menu"
            >
              <div className="user-avatar-pic">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                  <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="currentColor"/>
                </svg>
              </div>
            </button>

            {showDropdown && (
              <div className="user-dropdown">
                {/* User Info */}
                <div className="dropdown-user-info">
                  <div className="dropdown-user-details">
                    <div className="dropdown-user-name">{user ? (user.username || user.displayName || 'User') : 'User'}</div>
                    <div className="dropdown-user-email">{user?.email || ''}</div>
                  </div>
                </div>

                {/* Theme Toggle */}
                <button className="dropdown-item" onClick={handleThemeToggle}>
                  {theme === 'light' ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
                      <line x1="12" y1="1" x2="12" y2="3" stroke="currentColor" strokeWidth="2"/>
                      <line x1="12" y1="21" x2="12" y2="23" stroke="currentColor" strokeWidth="2"/>
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" stroke="currentColor" strokeWidth="2"/>
                      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" stroke="currentColor" strokeWidth="2"/>
                      <line x1="1" y1="12" x2="3" y2="12" stroke="currentColor" strokeWidth="2"/>
                      <line x1="21" y1="12" x2="23" y2="12" stroke="currentColor" strokeWidth="2"/>
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" stroke="currentColor" strokeWidth="2"/>
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                  {theme === 'light' ? t.sidebar.darkMode : t.sidebar.lightMode}
                </button>

                {/* Language Toggle */}
                <button className="dropdown-item" onClick={handleLanguageToggle}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M2 12H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 2C14.5 4.73 15.9 8.24 16 12C15.9 15.76 14.5 19.27 12 22C9.5 19.27 8.1 15.76 8 12C8.1 8.24 9.5 4.73 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t.sidebar.language}
                </button>

                {/* About */}
                <button className="dropdown-item" onClick={handleAbout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="8" r="0.5" fill="currentColor" stroke="currentColor" strokeWidth="1"/>
                  </svg>
                  {t.sidebar.about}
                </button>

                {/* Settings */}
                <button className="dropdown-item" onClick={handleSettings}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5842 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6642 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.2579 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6642 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.01127 9.77251C4.28054 9.5799 4.48571 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t.sidebar.settings}
                </button>

                {/* Sign Out */}
                <button className="dropdown-item sign-out" onClick={handleSignOut}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t.sidebar.signOut}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Spacer to push sessions to appropriate position */}
      <div className="sidebar-spacer"></div>

      {/* Session List Section */}
      {isAuthenticated && (
        <div className="sidebar-sessions">
          <div className="mcp-token-entry">
            <button
              className="mcp-token-entry-button"
              onClick={() => handleOpenMcpTokenPanel('google_map_reviews')}
              aria-label="Get Google Map Reviews MCP token"
              title={isCollapsed ? "Google Map Reviews MCP" : undefined}
            >
              <KeyRound size={16} aria-hidden="true" />
              {!isCollapsed && "Google Map Reviews MCP"}
            </button>
            <button
              className="mcp-token-entry-button"
              onClick={() => handleOpenMcpTokenPanel('cmi_insights')}
              aria-label="Get CMI Insights MCP token"
              title={isCollapsed ? "CMI Insights MCP" : undefined}
            >
              <KeyRound size={16} aria-hidden="true" />
              {!isCollapsed && "CMI Insights MCP"}
            </button>
            {hasRosettaAccess && (
              <button
                className="mcp-token-entry-button"
                onClick={() => handleOpenMcpTokenPanel('rosetta')}
                aria-label="Get Rosetta MCP token"
                title={isCollapsed ? "Rosetta MCP" : undefined}
              >
                <KeyRound size={16} aria-hidden="true" />
                {!isCollapsed && "Rosetta MCP"}
              </button>
            )}
            {hasYoutubeRetailAnalyzerAccess && (
              <button
                className="mcp-token-entry-button"
                onClick={() => handleOpenMcpTokenPanel('youtube_retail_analyzer')}
                aria-label="Get YouTube Retail Analyzer MCP token"
                title={isCollapsed ? "YouTube Retail Analyzer MCP" : undefined}
              >
                <KeyRound size={16} aria-hidden="true" />
                {!isCollapsed && "YouTube Retail Analyzer MCP"}
              </button>
            )}
          </div>

          {/* Always show New Chat button */}
          <div className="sessions-header">
            {!isCollapsed && <h3>Chat History</h3>}
            <button
              className="new-chat-button"
              onClick={handleNewChat}
              aria-label="Start new chat"
              title={isCollapsed ? "Start new chat" : undefined}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {!isCollapsed && "New chat"}
            </button>
          </div>

          {/* Only show session content when not collapsed */}
          {!isCollapsed && (
            <>
              {/* Loading State - Skeleton Cards for better UX */}
              {sessionsLoading && (
                <div className="sessions-loading">
                  {/* Show 3 skeleton cards to indicate loading sessions */}
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="loading-skeleton-card">
                      <div className="skeleton-title"></div>
                      <div className="skeleton-text"></div>
                    </div>
                  ))}
                </div>
              )}

              {/* Error State - Don't show if it's an authentication error */}
              {sessionsError && !sessionsLoading && sessionsError !== 'authentication_failed' && (
                <div className="sessions-error">
                  <p className="error-message">{sessionsError}</p>
                  <button className="retry-button" onClick={handleRetryFetch}>
                    Retry
                  </button>
                </div>
              )}

              {/* Sessions List */}
              {!sessionsLoading && (!sessionsError || sessionsError === 'authentication_failed') && sessions.length > 0 && (
                <div className="sessions-list auto-hide-scrollbar" ref={sessionsListRef}>
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      className={`session-card ${currentSessionId === session.id ? 'active' : ''} ${session.titleUpdating ? 'title-updating' : ''} ${deletingSessionId === session.id ? 'deleting' : ''}`}
                      onClick={() => handleSessionClick(session.id)}
                      onMouseLeave={handleSessionMouseLeave}
                    >
                      <div className="session-content">
                        <h4 className="session-title">
                          {session.titleUpdating ? (
                            <TypewriterTitle text={session.title} speed={50} />
                          ) : (
                            session.title
                          )}
                        </h4>
                        <div className={`session-actions ${fadingOutDeleteId === session.id ? 'fading-out' : ''}`}>
                          {confirmingDeleteId === session.id ? (
                            // Show confirm/cancel buttons when confirming delete
                            <>
                              <button
                                className="session-confirm-delete-btn"
                                onClick={(e) => handleConfirmDelete(session.id, e)}
                                aria-label="Confirm delete"
                                disabled={deletingSessionId === session.id}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              <button
                                className="session-cancel-delete-btn"
                                onClick={handleCancelDelete}
                                aria-label="Cancel delete"
                                disabled={deletingSessionId === session.id}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            </>
                          ) : (
                            // Show time and delete button normally
                            <>
                              <p className="session-time">{session.lastActivity}</p>
                              <button
                                className="session-delete-btn"
                                onClick={(e) => handleDeleteSession(session.id, e)}
                                aria-label="Delete session"
                                disabled={deletingSessionId === session.id}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Loading sentinel for intersection observer */}
                  {sessionsHasMore && (
                    <div ref={loadingSentinelRef} className="sessions-loading-sentinel">
                      {sessionsLoadingMore && (
                        <div className="sessions-loading-more">
                          <div className="spinner-circle-small"></div>
                          <span className="loading-more-text">Loading more...</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Empty State - Only show when definitely no sessions and not loading */}
              {!sessionsLoading && !sessionsError && sessions.length === 0 && (
                <div className="sessions-empty">
                  <div className="empty-icon">💬</div>
                  <p>No chat history yet</p>
                  <small>Start a conversation to see your chat history here</small>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* About Modal */}
      <AboutModal isOpen={showAboutModal} onClose={() => setShowAboutModal(false)} />
      <MCPTokenPanel
        isOpen={showMcpTokenPanel}
        onClose={() => setShowMcpTokenPanel(false)}
        {...mcpTokenPanelConfig}
      />

    </div>
  );
};

export default Sidebar;
